import { __rest } from "tslib";
import cookie from '@tencent/vft-common/cookie';
import env from '@tencent/vft-common/env';
import { Logger } from '@tencent/vft-common/logger';
import KVJoiner from '@tencent/vft-cosmos/base/KVJoiner';
import KVParser from '@tencent/vft-cosmos/base/KVParser';
import { BINDING_TYPES, DEFAULT_BUSINESS, DEFAULT_MODULE_TYPE, MODULE_FIELD_NAME } from './const';
import { BindingTypes } from './defs';
export function isFunction(fn) {
    return typeof fn === 'function';
}
const kvParser = new KVParser({ canSkipEmpty: true });
/**
 * @description 将a=1&b=2形式的字符串解析成{a:1, b:2}对象
 * @param {String|Record<string, any>} query 参数
 */
export function query2obj(query) {
    return typeof query === 'string' ? kvParser.parse(query) : query;
}
const kvJoiner = new KVJoiner();
/**
 * @description 将对象用=和&拼接成字符串
 * @param {Record<string, any>} o 待拼接的对象
 * @returns {String}
 */
export function obj2query(o) {
    return kvJoiner.add(o).join();
}
function generatePvid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return [s4(), s4(), s4(), s4()].join('');
}
let pvid;
export function getPvid() {
    pvid = pvid || cookie.get('pgv_pvid');
    if (!pvid || pvid.length < 8) {
        // 兜底，如果取不到 pvid 情况下，生成一个pvid
        pvid = generatePvid();
        const domain = /wetv\.vip/.test(location.hostname) ? 'wetv.vip' : 'qq.com';
        cookie.set('pgv_pvid', pvid, {
            expires: 2592000,
            domain,
            path: '/',
        });
    }
    return pvid;
}
// 为了方便单元测试
export function resetPvid() {
    pvid = '';
}
/**
 * 获取上报类型
 * @param {VftReport.DirectiveModifiers} modifiers 指令修饰符
 *
 * @return {VftReport.BindingType}
 */
export function extractBindingType(modifiers) {
    return Object.keys(modifiers).find((name) => BINDING_TYPES.includes(name)) || BindingTypes.ALL;
}
/**
 * 处理Vue绑定指令
 * @param binding Vue指令绑定数据
 * @param hook 钩子
 * @returns BindingData
 */
export function parseVueBindingValue(binding, hook) {
    let { value } = binding;
    if (typeof value === 'string') {
        value = query2obj(value);
    }
    const { modifiers, arg } = binding;
    /*
     * 快捷处理模块上报
     * <div v-report.module="{模块: { f_module: 'xxx' }}"></div>
     * 上报数据为 { f_module: 'xxx' }
     */
    const type = extractBindingType(modifiers);
    if (type === BindingTypes.MODULE && value[MODULE_FIELD_NAME]) {
        value = value[MODULE_FIELD_NAME];
    }
    return {
        type,
        value,
        hook,
        once: modifiers.once,
        modifiers,
        module: arg,
    };
}
export var ScreenType;
(function (ScreenType) {
    ScreenType["REGULAR"] = "regular";
    ScreenType["LARGE"] = "large";
    ScreenType["HUGE"] = "huge";
    ScreenType["MAX"] = "max";
})(ScreenType || (ScreenType = {}));
const screenConfigs = [
    [ScreenType.REGULAR, 375],
    [ScreenType.LARGE, 667],
    [ScreenType.HUGE, 980],
    [ScreenType.MAX, 1366],
];
/**
 *
 * @returns {ScreenType}
 */
export function calculateScreenType() {
    const realScreenWidth = calculateDeviceRealSize(document.documentElement.clientWidth, window.devicePixelRatio);
    return getScreenType(realScreenWidth);
}
export function getScreenType(realScreenWidth) {
    for (const [type, size] of screenConfigs) {
        if (size > realScreenWidth) {
            return type;
        }
    }
    // 如果都小于屏幕尺寸，则返回max
    return screenConfigs[screenConfigs.length - 1][0];
}
export function calculateDeviceRealSize(distance = document.documentElement.clientWidth, dpr = window.devicePixelRatio) {
    let dpi = 1;
    let fixedDpr = dpr;
    if (dpr > 1 && dpr < 3) {
        dpi = 2;
    }
    else if (dpr >= 3) {
        dpi = 3;
    }
    /*
      2020.5.12
      普通屏幕是160ppi，也就是我们常说的1倍屏幕。
      如果是320ppi，也就是我们常说的2倍屏幕。
      现在所有的设备都是160ppi+，高清的iphone XM 就是458ppi，虽然dpr是3，但其实是不够3倍屏的，所以还是以2倍屏来处理。
      2020.5.26
      有某些特殊的安卓机型dpr居然是4，所以有了以下判断，
    */
    if (dpr >= 4 && env.isAndroid) {
        fixedDpr = 2;
    }
    return fixedDpr * distance / dpi;
}
const boss2dtKeyMap = {
    f_module_id: 'mod_id',
    f_module: 'mod_title',
    f_resource: 'btn_title',
    f_module_type: 'e_module_type',
};
const dt2bossKeyMap = {
    eid: 'f_hottag',
    btn_title: 'f_resource',
    mod_title: 'f_module',
    mod_id: 'f_hottag',
};
export function mapParams(params, mapConfig) {
    const mappedParams = {};
    Object.keys(params).forEach((key) => {
        const mappedKey = mapConfig[key] || key;
        mappedParams[mappedKey] = params[key];
    });
    return mappedParams;
}
/**
 *
 * @param {ReportParams} params 上报参数
 * @returns
 */
export function mapBoss2DtParams(params) {
    return mapParams(params, boss2dtKeyMap);
}
export function mapDt2BossParams(params) {
    return mapParams(params, dt2bossKeyMap);
}
export function normalizeDtParams(params) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const _a = mapBoss2DtParams(params), { f_hottag, eid, e_module_type = DEFAULT_MODULE_TYPE } = _a, rests = __rest(_a, ["f_hottag", "eid", "e_module_type"]);
    return Object.assign({ e_module_type, business: DEFAULT_BUSINESS }, rests);
}
export const logger = new Logger('vft-report');
/**
 * 判断是否是类Promise对象
 * @param {any} target
 * @returns Boolean
 */
export function isPromisable(target) {
    if (!target) {
        return false;
    }
    return isFunction(target.then) && isFunction(target.catch);
}
/**
 * location.pathname有三种等价的格式
 * 1. /xx/index.html
 * 2. /xx/
 * 3. /xx
 * 此函数将传入的pathname标准化为第二种格式
 * @param {String} pathname 原始的pathname，一般取location.pathname
 * @returns {String} 标准化后的pathname
 */
export function normalizePath(pathname) {
    if (/\/$/.test(pathname)) { // 以/结尾的直接返回
        return pathname;
    }
    const matches = pathname.match(/(.*\/)(.*?(\.html)?)$/);
    if (!matches) {
        return pathname;
    }
    const [match, path, file, extname] = matches;
    // 以具体html文件名结尾的
    if (extname === '.html') {
        // 如果是index.html，则去掉index.html，如果是其它文件名，返回原路径
        return file === 'index.html' ? path : match;
    }
    // 否则就是以目录结尾，并且尾部没有/，需要补齐/
    return `${path}${file}/`;
}
